import { useSet } from 'custom_hooks/useSet';
import { FaultTypeNode } from 'pages/Desk/issues/fault_type/FaultTypeNode';
import {
    filterFaultTypes,
    IssueFaultType,
} from 'pages/Desk/issues/fault_type/useFaultTypes';
import { StyledSearchInput } from 'pages/Desk/issues/filter_pane/styled';
import { IssueMode } from 'pages/Desk/issues/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DataTree } from 'shared/data_tree/DataTree';
import { Tree, TreeExpansionConfig, TreeSelectionConfig } from 'shared/data_tree/types';
import { addMissingItems, excludeNullAndUndefined, removeAllItems } from 'utils/array';

export type FaultTypesTreeProps = Readonly<{
    issueMode: IssueMode;
    faultTypesTree: Tree<IssueFaultType>;
    selectedFaultTypeIds: string[];
    onSelectedChange: (faultTypeIds: string[]) => void;
}>;

export function FaultTypesTree({
    issueMode,
    faultTypesTree,
    selectedFaultTypeIds,
    onSelectedChange,
}: FaultTypesTreeProps) {
    const [filterText, setFilterText] = useState('');
    const [expandedFaultTypeIds, setExpandedFaultTypeIds] = useSet<string>(() => {
        const selectedNodes = excludeNullAndUndefined(
            selectedFaultTypeIds.map((id) =>
                faultTypesTree.has(id) ? faultTypesTree.get(id) : undefined,
            ),
        );
        const parentIds = selectedNodes.flatMap(({ hierarchy }) =>
            hierarchy.map(({ id }) => id),
        );
        return new Set(parentIds);
    });

    const onSelectedChangeRef = useRef(onSelectedChange);
    onSelectedChangeRef.current = onSelectedChange;

    const finalFilterText = useMemo(() => filterText.trim().toLowerCase(), [filterText]);

    const filteredFaultTypesTree = useMemo(
        () =>
            finalFilterText
                ? filterFaultTypes(faultTypesTree, finalFilterText)
                : undefined,
        [faultTypesTree, finalFilterText],
    );

    const expandable = useMemo<TreeExpansionConfig<IssueFaultType>>(
        () => ({
            isExpanded: ({ data: faultType }) => expandedFaultTypeIds.has(faultType.id),
            onChange: ({ node: { data: faultType } }) =>
                setExpandedFaultTypeIds.toggle(faultType.id),
            indentNonExpandableRoots: true,
        }),
        [expandedFaultTypeIds, setExpandedFaultTypeIds],
    );

    const selectable = useMemo<TreeSelectionConfig<IssueFaultType>>(
        () => ({
            selectionStyle: 'checkbox',
            isSelected: ({ id, allChildren }) =>
                selectedFaultTypeIds.includes(id) ||
                (issueMode === 'hardwareHealth' &&
                    allChildren.some((child) => selectedFaultTypeIds.includes(child.id))),
            onChange: ({ node, isSelected }) => {
                const childIds = node.allChildren.map(({ id }) => id);
                const parentIds = node.hierarchy.map(({ id }) => id);

                if (isSelected) {
                    if (issueMode === 'hardwareHealth') {
                        // select node, children, and parents
                        onSelectedChangeRef.current(
                            addMissingItems(selectedFaultTypeIds, [
                                node.id,
                                ...childIds,
                                ...parentIds,
                            ]),
                        );
                    } else {
                        // select only this node
                        onSelectedChangeRef.current([...selectedFaultTypeIds, node.id]);
                    }
                } else {
                    // deselect node (and children in hardware health mode)
                    const idsToRemove =
                        issueMode === 'hardwareHealth'
                            ? [node.id, ...childIds]
                            : [node.id];

                    onSelectedChangeRef.current(
                        removeAllItems(selectedFaultTypeIds, idsToRemove),
                    );
                }
            },
        }),
        [issueMode, selectedFaultTypeIds],
    );

    useEffect(() => {
        onSelectedChangeRef.current(selectedFaultTypeIds);
    }, [selectedFaultTypeIds]);

    useEffect(() => {
        if (filteredFaultTypesTree) {
            const expandedNodeIds = filteredFaultTypesTree.allNodes
                .filter(({ data }) => data.name.toLowerCase().includes(finalFilterText))
                .flatMap(({ hierarchy }) => hierarchy)
                .map(({ id }) => id);
            setExpandedFaultTypeIds.set(() => new Set(expandedNodeIds));
        }
    }, [filteredFaultTypesTree, finalFilterText, setExpandedFaultTypeIds]);

    const selectedIds = useMemo(
        () => new Set(selectedFaultTypeIds),
        [selectedFaultTypeIds],
    );

    return (
        <>
            <StyledSearchInput
                placeholder="Search Fault Types"
                value={filterText}
                onChange={(event) => setFilterText(event.target.value)}
            />
            <DataTree
                treeId="fault-types-filter-tree"
                data={filteredFaultTypesTree ?? faultTypesTree}
                expandable={expandable}
                selectable={selectable}
                renderNode={({ node }) => (
                    <FaultTypeNode
                        node={node}
                        filterText={finalFilterText}
                        // this is inefficient because all nodes must rerender
                        // if selectedIds change, but `selectable` probably
                        // already forces that, so :/
                        selectedIds={selectedIds}
                        handleSelect={(ids) =>
                            onSelectedChangeRef.current(
                                addMissingItems(selectedFaultTypeIds, ids),
                            )
                        }
                        handleDeselect={(ids) =>
                            onSelectedChangeRef.current(
                                removeAllItems(selectedFaultTypeIds, ids),
                            )
                        }
                        showChildrenSelector
                    />
                )}
            />
        </>
    );
}
